/**
 * Pure TEA update for the new-project name dialog (Splash + EditorShell).
 *
 * Shells interpret `create` Cmd (Tauri create + navigate) and feed back
 * CreateSucceeded / CreateFailed.
 */
import { assertNever } from "../lib/assertNever";
import type { UpdateFn } from "../lib/tea";

export type NewProjectDialogModel = {
  readonly open: boolean;
  readonly draft: string;
  readonly error: string;
  readonly busy: boolean;
};

export const initialNewProjectDialogModel: NewProjectDialogModel = {
  open: false,
  draft: "",
  error: "",
  busy: false,
};

export type NewProjectDialogMsg =
  | { readonly type: "Open" }
  | { readonly type: "Cancel" }
  | { readonly type: "DraftChanged"; readonly draft: string }
  | { readonly type: "Commit" }
  | { readonly type: "CreateSucceeded" }
  | { readonly type: "CreateFailed"; readonly error: string }
  /** Folder picker dismissed or other non-error abort; keep dialog open. */
  | { readonly type: "CreateAborted" };

export type NewProjectDialogCmd = {
  readonly kind: "create";
  readonly name: string;
};

const closed = initialNewProjectDialogModel;

export function updateNewProjectDialog(
  model: NewProjectDialogModel,
  msg: NewProjectDialogMsg,
): readonly [NewProjectDialogModel, readonly NewProjectDialogCmd[]] {
  switch (msg.type) {
    case "Open":
      if (model.busy) return [model, []];
      return [{ open: true, draft: "", error: "", busy: false }, []];
    case "Cancel":
      if (model.busy) return [model, []];
      return [closed, []];
    case "DraftChanged":
      return [{ ...model, draft: msg.draft, error: "" }, []];
    case "Commit": {
      if (model.busy) return [model, []];
      const name = model.draft.trim();
      if (name.length === 0) {
        return [{ ...model, error: "Enter a project name." }, []];
      }
      return [{ ...model, busy: true, error: "" }, [{ kind: "create", name }]];
    }
    case "CreateSucceeded":
      return [closed, []];
    case "CreateFailed":
      return [{ ...model, busy: false, error: msg.error }, []];
    case "CreateAborted":
      return [{ ...model, busy: false }, []];
    default:
      return assertNever(msg);
  }
}

export const updateNewProjectDialogFn: UpdateFn<
  NewProjectDialogModel,
  NewProjectDialogMsg,
  NewProjectDialogCmd
> = updateNewProjectDialog;
